import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";

const STORAGE_KEY = "wc_cookie_consent_v1";

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        // small delay so it doesn't pop in during initial paint
        const t = setTimeout(() => setVisible(true), 600);
        return () => clearTimeout(t);
      }
    } catch (_) {
      setVisible(true);
    }
  }, []);

  // Reserve space at the bottom of the page while the banner is visible,
  // so it never overlaps the last content (e.g. contact email).
  useEffect(() => {
    const cls = "cookie-banner-visible";
    if (visible) {
      document.documentElement.classList.add(cls);
    } else {
      document.documentElement.classList.remove(cls);
    }
    return () => document.documentElement.classList.remove(cls);
  }, [visible]);

  const persist = (value) => {
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ choice: value, ts: new Date().toISOString() })
      );
    } catch (_) {
      /* ignore */
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-banner"
      role="dialog"
      aria-live="polite"
      aria-label="Cookie-Hinweis"
      className="fixed bottom-0 left-0 right-0 z-[60] px-4 md:px-8 pb-4 md:pb-6 pointer-events-none"
    >
      <div className="container-x !px-0">
        <div className="pointer-events-auto relative bg-[#0a0a0a]/95 backdrop-blur-xl border border-white/10 shadow-2xl">
          <span className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37]/40 to-transparent" />
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 p-6 md:p-8">
            <div className="md:col-span-8">
              <div className="overline mb-3 flex items-center gap-3">
                <span className="block w-6 h-px bg-[#D4AF37]" />
                Datenschutz & Cookies
              </div>
              <p className="text-sm text-zinc-300 leading-relaxed font-light">
                Wir verwenden ausschließlich technisch notwendige Cookies, um
                den sicheren Betrieb dieser Website zu gewährleisten. Optionale
                Cookies oder Tracking-Dienste setzen wir nur nach Ihrer
                ausdrücklichen Einwilligung ein. Weitere Informationen finden
                Sie in unserer{" "}
                <Link
                  to="/datenschutz"
                  data-testid="cookie-banner-policy-link"
                  className="text-[#D4AF37] hover:underline underline-offset-4"
                >
                  Datenschutzerklärung
                </Link>
                .
              </p>
            </div>
            <div className="md:col-span-4 flex flex-col sm:flex-row md:flex-col gap-3 md:items-stretch md:justify-center">
              <button
                type="button"
                data-testid="cookie-accept"
                onClick={() => persist("accepted")}
                className="inline-flex items-center justify-center bg-[#D4AF37] text-black hover:bg-[#c19c30] transition-colors duration-300 px-6 py-3 uppercase tracking-[0.24em] text-[11px] font-semibold"
              >
                Alle akzeptieren
              </button>
              <button
                type="button"
                data-testid="cookie-reject"
                onClick={() => persist("rejected")}
                className="inline-flex items-center justify-center border border-white/25 text-white hover:border-white hover:bg-white/5 transition-colors duration-300 px-6 py-3 uppercase tracking-[0.24em] text-[11px] font-semibold"
              >
                Nur notwendige
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={() => persist("rejected")}
            data-testid="cookie-dismiss"
            aria-label="Hinweis schließen"
            className="absolute top-3 right-3 p-2 text-zinc-500 hover:text-white transition-colors"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </div>
  );
}
